import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from "@angular/core";
import { Router } from "@angular/router";
import { DateTime } from "luxon";
import { forkJoin, Observable } from "rxjs";
import {
  MetricElement,
  ShowerheadsGraphStats,
} from "src/app/core/models/global-stats.model";
import { User } from "src/app/core/models/user.model";
import { GlobalStatsService } from "src/app/core/services/global-stats.service";
import { FiltersStore } from "src/app/core/stores/filters.store";
import { UserStore } from "src/app/core/stores/user.store";
import { GraphGrouping } from "src/app/shared/types/hydrao-types";
import { UtilsService } from "../../shared/services/utils.service";
import { StatsElement, StatsMap } from "../models/placestats.model";
import { OrderByDirection, OrderByItem } from "../models/search-result.model";
import {
  ShowerHead,
  ShowerHeadConnectivity,
  ShowerheadData,
  ShowerHeadType,
} from "../models/shower-head.model";
import { Shower } from "../models/shower.model";
import {
  ShowerheadSignalElement,
  ShowerheadSignalsMap,
} from "../models/showerhead-signal.model";
import { Threshold, ThresholdColor } from "../models/threshold.model";
import { ShowerHeadService } from "../services/shower-head.service";
import { ShowerService } from "../services/shower.service";
import {
  AppStore,
  PlaceTourScenario,
  ShowerheadTourScenario,
} from "./app.store";
import { AuthStore } from "./auth.store";
import { GatewayStore } from "./gateway.store";
import { PlacesStore } from "./places.store";

enum ShScreenMode {
  Normal = "normal",
  Loading = "loading",
}

const LOG_TAG = "[SH-STORE] ";

// @Injectable()
@Injectable({
  providedIn: "root",
})
export class ShowerheadStore {
  protected utilsService: UtilsService = inject(UtilsService);
  protected globalStatsService: GlobalStatsService = inject(GlobalStatsService);
  protected showerheadService: ShowerHeadService = inject(ShowerHeadService);
  protected showerService: ShowerService = inject(ShowerService);
  protected authStore: AuthStore = inject(AuthStore);
  protected filtersStore: FiltersStore = inject(FiltersStore);
  protected userStore: UserStore = inject(UserStore);
  protected appStore: AppStore = inject(AppStore);
  protected placesStore: PlacesStore = inject(PlacesStore);
  protected gatewayStore: GatewayStore = inject(GatewayStore);
  private router: Router = inject(Router);

  private screenMode: ShScreenMode = ShScreenMode.Normal;

  // Define signals for the store state
  private readonly forceRefresh = signal<boolean>(false);
  private readonly error = signal<string | null>(null);
  readonly error$ = this.error.asReadonly();

  // ---------------------------
  // --- touring
  // ---------------------------

  readonly touring = {
    welcomeStep: {
      url: "showerheads/stats",
      title: $localize`:@@showerheads.touring.welcomeTitle:Welcome to showerheads section`,
      text: $localize`:@@showerheads.touring.welcomeText:You will learn how to track the water consumption of your showerheads, as well as manage them. Let's start with consumption tracking.`,
    },
    placeFilterStep: {
      url: "showerheads/stats",
      title: $localize`:@@showerheads.touring.shByPlaceTitle:Showerheads organized by place`,
      text: $localize`:@@showerheads.touring.shByPlaceText:You can group your showerheads by location to make navigation easier. This filter allows you to display only the devices of the selected location.`,
    },
    periodFilterStep: {
      url: "showerheads/stats",
      title: $localize`:@@showerheads.touring.periodSelectionTitle:Choice of the period`,
      text: $localize`:@@showerheads.touring.periodSelectionText:Whether for the entire year or the details of a single day, feel free to explore the possibilities.`,
    },
    showerheadFilterStep: {
      url: "showerheads/stats",
      title: $localize`:@@showerheads.touring.myShowerheadsTitle:My showerheads`,
      text: $localize`:@@showerheads.touring.myShowerheadsText:Select the showerhead(s) whose consumption you want to display. Some more detailed information are only visible if a single showerheadd is selected.`,
    },
    statsStep: {
      url: "showerheads/stats",
      title: $localize`:@@showerheads.touring.consumptionTitle:Your consumption`,
      text: $localize`:@@showerheads.touring.consumptionText:You can find the history of your showerheads's consumption based on the filters seen previously. To see more details, select a bar on the chart by clicking on it.`,
    },
    pageHeaderNavStep: {
      url: "showerheads/stats",
      title: $localize`:@@showerheads.touring.changePageTitle:Let’s go to device page`,
      text: $localize`:@@showerheads.touring.changePageText:To change the page, simply click on the title and select 'Devices'.`,
    },
    devicesStep: {
      url: "showerheads/devices",
      title: $localize`:@@showerheads.touring.showerheadsManagementTitle:Management of your showerheads`,
      text: $localize`:@@showerheads.touring.showerheadsManagementText:You can see the list of your showerheads, view their information, rename them and add new ones.`,
    },
  };

  readonly tourSteps$ = computed(() => {
    const tourSteps = [];

    Object.entries(this.touring).forEach(([step, data]) => {
      tourSteps.push(step + "@" + data.url);
    });

    return tourSteps;
  });

  getTourTitle(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.title : "";
  }

  getTourText(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.text : "";
  }

  // ---------------------------
  // --- page header
  // ---------------------------

  readonly headers$ = computed(() => {
    const headers = [];

    headers.push({
      id: "consumption",
      label: $localize`Consumption tracking`,
      url: "/showerheads",
    });

    headers.push({
      id: "equipments",
      label: $localize`Devices`,
      url: "/showerheads/devices",
    });

    return headers;
  });

  // ---------------------------
  // --- showerheads
  // ---------------------------

  private tourAddId: number = 90;
  private readonly showerheadsLastFetch = signal<Date>(null);
  private showerheadsRefreshDelay: number = 3600; // 1 hour
  private maxThreshold = signal<number>(null);
  readonly maxThreshold$ = this.maxThreshold.asReadonly();

  private readonly showerheads = signal<ShowerHead[]>([]);
  private tourShowerheads: ShowerHead[] = [];
  private readonly showerheadsLoading = signal<boolean>(false);
  readonly showerheadsLoading$ = computed(() => {
    return (
      this.showerheadsLastFetch() === null || this.showerheadsLoading() === true
    );
  });

  private readonly showerheadsDataById = signal<Record<string, ShowerheadData>>(
    {}
  );

  private readonly showerheadsById = signal<Record<string, ShowerHead>>({});
  private tourShowerheadsById: Record<string, ShowerHead> = {};

  readonly showerheadsByGatewayId$ = computed(() => {
    const showerheadsById = this.showerheadsById$();

    if (!showerheadsById || Object.keys(showerheadsById).length === 0)
      return {};

    const result: Record<string, ShowerHead[]> = {};

    Object.values(showerheadsById).forEach((sh) => {
      if (typeof sh.gateway_uuid === "string" && sh.gateway_uuid.length > 0) {
        if (!Array.isArray(result[sh.gateway_uuid])) {
          result[sh.gateway_uuid] = [];
        }
        result[sh.gateway_uuid].push(sh);
      }
    });

    return result;
  });

  readonly _showerheadsById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourShowerheadsById : this.showerheadsById();
  });

  readonly showerheadsById$ = computed(() => {
    const showerheadsById = this._showerheadsById$();
    const showerheadsDataById = this.showerheadsDataById();

    const result: Record<string, ShowerHead> = {};

    Object.entries(showerheadsById).forEach(([shUuid, sh]) => {
      const finalSh = sh.clone();
      const data = showerheadsDataById[sh.device_uuid];
      if (data) {
        finalSh.setData(data);
      }

      result[sh.device_uuid] = finalSh;
    });

    return result;
  });

  readonly _showerheads$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourShowerheads : this.showerheads();
  });

  readonly showerheads$ = computed(() => {
    const showerheads = this._showerheads$();
    const showerheadsDataById = this.showerheadsDataById();

    const result: ShowerHead[] = [];

    showerheads.forEach((sh) => {
      const finalSh = sh.clone();
      const data = showerheadsDataById[sh.device_uuid];
      if (data) {
        finalSh.setData(data);
      }

      result.push(finalSh);
    });

    return result;
  });

  readonly showerheadUuids$ = computed(() => {
    const showerheadsById = this.showerheadsById$();
    return Object.keys(showerheadsById);
  });

  readonly showerheadsCount$ = computed(() => {
    const showerheadUuids = this.showerheadUuids$();

    return showerheadUuids.length;
  });

  readonly noShowerheads$ = computed(() => {
    const showerheadsLoading = this.showerheadsLoading$();
    const showerheadsCount = this.showerheadsCount$();

    return showerheadsLoading === false ? showerheadsCount === 0 : false;
  });

  readonly showerheadsByPlaceId$ = computed(() => {
    const showerheadsById = this.showerheadsById$();

    if (!showerheadsById || Object.keys(showerheadsById).length === 0)
      return {};

    const result: Record<number, ShowerHead[]> = {};

    Object.values(showerheadsById).forEach((sh) => {
      let placeId = sh.place_id;
      if (typeof sh.place_id !== "number") {
        placeId = -1;
      }

      if (!Array.isArray(result[placeId])) {
        result[placeId] = [];
      }
      result[placeId].push(sh);
    });

    return result;
  });

  fetchShowerheads(forceRefresh: boolean = false) {
    try {
      if (this.showerheadsLoading()) return;

      if (
        forceRefresh ||
        this.showerheadsLastFetch() === null ||
        this.utilsService.calculateElapsedSeconds(this.showerheadsLastFetch()) >
          this.showerheadsRefreshDelay
      ) {
        this.showerheadsLoading.set(true);
        console.log(LOG_TAG + "fetchShowerheads...");

        this.showerheadService.getShowerHeadsForStore().subscribe({
          next: (showerheads: ShowerHead[]) => {
            this.showerheads.set(showerheads);
            this.showerheadsById.set(
              this._computeIndexedShowerheads(showerheads)
            );
            if (this.screenMode !== ShScreenMode.Loading) {
              this.showerheadsLoading.set(false);
            }

            this.showerheadsLastFetch.set(new Date());
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to fetch showerheads : ", err);
            this.error.set(err.message || "An error occurred.");
            this.showerheadsLoading.set(false);
          },
        });
      }
    } catch (error) {
      console.error(LOG_TAG + "ERROR ON FETCH SHOWERHEADS", error);
    }
  }

  getFirstSeen(showerheadUuids: string[]): DateTime {
    let result: DateTime = null;

    const showerheads = this.showerheadsById$();
    if (!showerheads || Object.keys(showerheads).length === 0) return result;

    // compute firstSeen from showerheads
    showerheadUuids.map((showerheadUuid) => {
      const showerhead: ShowerHead = showerheads[showerheadUuid];
      // console.log(LOG_TAG + "GET FIRST SEEN", showerhead.label, showerhead.first_seen, result);

      if (
        showerhead &&
        showerhead.first_seen !== null &&
        (!result || result > showerhead.first_seen)
      ) {
        result = showerhead.first_seen;
      }
    });

    return result;
  }

  getLastSeen(showerheadUuids: string[]): DateTime {
    let result = null;

    const showerheads = this.showerheadsById$();
    if (!showerheads || Object.keys(showerheads).length === 0) return result;

    // compute lastSeen from showerheads
    showerheadUuids.map((showerheadUuid) => {
      const showerhead: ShowerHead = showerheads[showerheadUuid];
      // console.log(LOG_TAG + "GET LAST SEEN", showerhead.label, showerhead.last_seen, result);

      if (
        showerhead &&
        showerhead.last_seen !== null &&
        (!result || result < showerhead.last_seen)
      ) {
        result = showerhead.last_seen;
      }
    });

    return result;
  }

  createDataFromUuid(showerhead_uuid: string) {
    const showerheadsById = this.showerheadsById$();
    return showerheadsById[showerhead_uuid]
      ? showerheadsById[showerhead_uuid]
      : null;
  }

  // ---------------------------
  // --- showerhead actions
  // ---------------------------

  private readonly adding = signal<boolean>(false);
  readonly adding$ = this.adding.asReadonly();
  private readonly addResult = signal<ShowerHead | string | null>(null);
  readonly addResult$ = this.addResult.asReadonly();

  addShowerhead(serial: string, label: string, placeId?: number) {
    if (this.adding()) return;

    this.addResult.set(null);
    this.adding.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // check serial not already in store
      let exists = Object.entries(this.tourShowerheadsById).filter(
        ([showerheadUuid, showerhead]) => showerhead.serial === serial
      );
      if (exists && exists.length > 0) {
        this.addResult.set("already_registered");
      }

      // prepare new showerhead
      const addedShowerhead = this._createShowerhead(
        "" + this.tourAddId,
        label,
        placeId,
        null
      );
      addedShowerhead.serial = serial;

      // update stores
      this.tourShowerheadsById[addedShowerhead.device_uuid] = addedShowerhead;
      this.tourAddId++;

      this.adding.set(false);
      this.addResult.set(addedShowerhead);
    } else {
      this.showerheadService
        .addShowerheadForStore(serial, label, placeId)
        .subscribe({
          next: (showerhead: ShowerHead) => {
            this.adding.set(false);
            this.addResult.set(showerhead);
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to add showerhead : ", err);
            this.addResult.set(err.error_code || "unknown");
            this.adding.set(false);
          },
        });
    }
  }

  declareAddSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh showerheads
      this.fetchShowerheads(true);
    }
    this.addResult.set(null);
  }

  resetAddResult() {
    this.addResult.set(null);
  }

  private readonly updating = signal<boolean>(false);
  readonly updating$ = this.updating.asReadonly();
  private readonly updateResult = signal<ShowerHead | string | null>(null);
  readonly updateResult$ = this.updateResult.asReadonly();

  updateShowerhead(
    showerheadUuid: string,
    label: string,
    placeId?: number,
    thresholds?: Threshold[]
  ) {
    if (this.updating()) return;

    this.updateResult.set(null);
    this.updating.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // get showerhead
      const updatedShowerhead = this.showerheadsById[showerheadUuid];

      // update showerhead props
      updatedShowerhead.label = label;
      updatedShowerhead.place_id = placeId;

      // update stores

      this.updating.set(false);
      this.updateResult.set(updatedShowerhead);
    } else {
      this.showerheadService
        .updateShowerheadForStore(showerheadUuid, label, placeId, thresholds)
        .subscribe({
          next: (showerhead: ShowerHead) => {
            this.updating.set(false);
            this.updateResult.set(showerhead);
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to update showerhead : ", err);
            this.updateResult.set(err.error_code || "unknown");
            this.updating.set(false);
          },
        });
    }
  }

  declareUpdateSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh showerheads
      this.fetchShowerheads(true);
    }
    this.updateResult.set(null);
  }

  resetUpdateResult() {
    this.updateResult.set(null);
  }

  private readonly multiUpdating = signal<boolean>(false);
  readonly multiUpdating$ = this.multiUpdating.asReadonly();
  private readonly multiUpdateResult = signal<boolean | string | null>(null);
  readonly multiUpdateResult$ = this.multiUpdateResult.asReadonly();

  updateShowerheads(
    showerheadUuids: string[],
    placeId?: number,
    thresholds?: Threshold[]
  ) {
    if (this.multiUpdating()) return;

    this.multiUpdateResult.set(null);
    this.multiUpdating.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // get showerheads
      //TODO
      // const updatedShowerhead = this.showerheadsById[showerheadUuid];

      // // update showerhead props
      // updatedShowerhead.label = label;
      // updatedShowerhead.place_id = placeId;

      // update stores

      this.multiUpdating.set(false);
      this.multiUpdateResult.set(true);
    } else {
      const updateType = Array.isArray(thresholds) ? "thresholds" : "placeId";
      this.showerheadService
        .updateShowerheadsForStore(
          showerheadUuids,
          updateType,
          placeId,
          thresholds
        )
        .subscribe({
          next: (result: ShowerHead) => {
            this.multiUpdating.set(false);
            this.multiUpdateResult.set(true);
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to update showerheads : ", err);
            this.multiUpdateResult.set(err.error_code || "unknown");
            this.multiUpdating.set(false);
          },
        });
    }
  }

  declareMultiUpdateSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh showerheads
      this.fetchShowerheads(true);
    }
    this.multiUpdateResult.set(null);
  }

  resetMultiUpdateResult() {
    this.multiUpdateResult.set(null);
  }

  private readonly removing = signal<boolean>(false);
  readonly removing$ = this.removing.asReadonly();
  private readonly removeResult = signal<boolean | string | null>(null);
  readonly removeResult$ = this.removeResult.asReadonly();

  removeShowerhead(showerheadUuid) {
    if (this.removing()) return;

    this.removeResult.set(null);
    this.removing.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // check meterUuid  in store
      let exists = Object.keys(this.tourShowerheadsById).filter(
        (uuid) => uuid === showerheadUuid
      );
      if (!exists || exists.length === 0) {
        this.removeResult.set("unauthorized");
      }

      // remove meter
      const meter = this.tourShowerheadsById[showerheadUuid];

      // update stores

      this.removing.set(false);
      this.removeResult.set(true);
    } else {
      this.showerheadService
        .removeShowerheadForStore(showerheadUuid)
        .subscribe({
          next: (showerhead: ShowerHead) => {
            this.removing.set(false);
            this.removeResult.set(true);
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to remove showerhead : ", err);
            this.removeResult.set(err.error_code || "unknown");
            this.removing.set(false);
          },
        });
    }
  }

  declareRemoveSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh showerheads
      this.fetchShowerheads(true);
    }
    this.removeResult.set(null);
  }

  resetRemoveResult() {
    this.removeResult.set(null);
  }

  // ---------------------------
  // --- sorting
  // ---------------------------

  private showerheadsOrderByTimer: any;

  private readonly showerheadsOrderBy = signal<string>("label-asc");
  readonly showerheadsOrderBy$ = this.showerheadsOrderBy.asReadonly();

  readonly sortsById$ = computed(() => {
    const sorts: OrderByItem[] = [
      {
        id: "label-asc",
        field: "label",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.aToZ:(A > Z)`,
      },
      {
        id: "label-desc",
        field: "label",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.zToA:(Z > A)`,
      },
      {
        id: "volume-asc",
        field: "volume",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.volume:Sorted on volume` + "\n" + "(- > +)",
      },
      {
        id: "volume-desc",
        field: "volume",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.volume:Sorted on volume` + "\n" + "(+ > -)",
      },
      {
        id: "lastseen-asc",
        field: "last_seen",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.comm:Sorted on last comm.` +
          "\n" +
          $localize`:@@global.sort.oldToNew:(oldest > newest)`,
      },
      {
        id: "lastseen-desc",
        field: "last_seen",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.comm:Sorted on last comm.` +
          "\n" +
          $localize`:@@global.sort.newToOld:(newest > oldest)`,
      },
    ];

    const sortItems = {};
    sorts.map((sort) => {
      sortItems[sort.id] = sort;
    });

    return sortItems;
  });

  readonly sorts$ = computed(() => {
    const sortsById = this.sortsById$();

    return Object.values(sortsById);
  });

  readonly currentSort$ = computed(() => {
    const sortsById = this.sortsById$();
    const orderBy = this.showerheadsOrderBy$();

    if (!orderBy) return null;

    return sortsById[orderBy];
  });

  updateShowerheadsOrderBy(newOrderBy: string): void {
    if (newOrderBy !== this.showerheadsOrderBy()) {
      if (this.showerheadsOrderByTimer) {
        clearTimeout(this.showerheadsOrderByTimer);
      }

      this.showerheadsOrderByTimer = setTimeout(() => {
        this.showerheadsOrderBy.set(newOrderBy);
      }, 100);
    }
  }

  // ---------------------------
  // --- filtering
  // ---------------------------

  private showerheadsSearchTimer: any;

  private readonly showerheadsSearch = signal<string>(null);
  readonly showerheadsSearch$ = this.showerheadsSearch.asReadonly();

  updateShowerheadsSearch(newSearch: string): void {
    if (newSearch !== this.showerheadsSearch()) {
      if (this.showerheadsSearchTimer) {
        clearTimeout(this.showerheadsSearchTimer);
      }

      this.showerheadsSearchTimer = setTimeout(() => {
        this.showerheadsSearch.set(newSearch);
      }, 100);
    }
  }

  readonly placeShowerheads$ = computed(() => {
    const placeId = this.filtersStore.placeId$();
    const showerheadsByPlaceId = this.showerheadsByPlaceId$();
    const showerheads = this.showerheads$();

    let placeIds = [];

    if (placeId !== null) {
      placeIds.push(placeId);

      const placeDescendantIds =
        this.placesStore.getPlaceDescendantIds(placeId);
      if (Array.isArray(placeDescendantIds) && placeDescendantIds.length > 0) {
        placeIds = [...placeIds, ...placeDescendantIds];
      }
    }

    let result = [];
    if (placeIds.length === 0) {
      result = showerheads;
    } else {
      placeIds.map((pId) => {
        if (showerheadsByPlaceId[pId]) {
          result = [...result, ...showerheadsByPlaceId[pId]];
        }
      });
    }

    return result;
  });

  readonly placeShowerheadUuids$ = computed(() => {
    const placeShowerheads = this.placeShowerheads$();

    return placeShowerheads.map((sh) => sh.device_uuid);
  });

  readonly deepPlaceShowerheadUuids$ = computed(() => {
    const placeId = this.filtersStore.placeId$();
    const placesById = this.placesStore.placesById$();
    const showerheadsById = this.showerheadsById$();
    const showerheadsCount = this.showerheadsCount$();

    if (showerheadsCount === 0) return [];

    const uuids = [];
    if (placeId === -1) {
      Object.values(showerheadsById).forEach((sh) => {
        if (sh.place_id === null) {
          if (uuids.indexOf(sh.device_uuid) < 0) {
            uuids.push(sh.device_uuid);
          }
        }
      });
      return uuids;
    } else if (placeId !== null) {
      const place = placesById[placeId];
      Object.values(showerheadsById).forEach((sh) => {
        if (
          sh.place_id !== null &&
          (place.id === sh.place_id ||
            place.deepChildrenIds.indexOf(sh.place_id) >= 0)
        ) {
          if (uuids.indexOf(sh.device_uuid) < 0) {
            uuids.push(sh.device_uuid);
          }
        }
      });
      return uuids;
    } else {
      return Object.keys(showerheadsById);
    }
  });

  readonly deepPlaceShowerheadsCount$ = computed(() => {
    const deepPlaceShowerUuids = this.deepPlaceShowerheadUuids$();
    const showerheadsCount = this.showerheadsCount$();

    if (showerheadsCount === 0) return 0;

    return deepPlaceShowerUuids.length;
  });

  readonly filteredShowerheads$ = computed(() => {
    const showerheadsById = this.showerheadsById$();
    let filteredShowerheads = this.placeShowerheads$();
    const search = this.showerheadsSearch();
    const stats = this.showerheadsStats$();
    const currentSort = this.currentSort$();
    const user = this.userStore.user$();

    if (filteredShowerheads.length === 0) return filteredShowerheads;

    filteredShowerheads = filteredShowerheads.map((sh) => sh.clone());

    // filter on search
    const fixedSearch =
      typeof search === "string" && search.trim().length > 0
        ? search.trim().toLowerCase()
        : null;
    if (fixedSearch) {
      filteredShowerheads = filteredShowerheads.filter(
        (sh) =>
          sh.universalLabel?.trim().toLowerCase().indexOf(fixedSearch) >= 0 ||
          sh.serial?.trim().toLowerCase().indexOf(fixedSearch) >= 0 ||
          (user.canViewPlaces() === true &&
            sh.placeLabel &&
            sh.placeLabel.trim().toLowerCase().indexOf(fixedSearch) >= 0)
      );
      if (filteredShowerheads.length === 0) return filteredShowerheads;
    }

    // apply order by
    if (currentSort) {
      let showerheadUuids = filteredShowerheads.map((sh) => sh.device_uuid);
      let orderedShowerheadUuids = [];
      let notOrderedShowerheadUuids = [...showerheadUuids];
      if (currentSort.field === "volume") {
        let sortedUuids;
        // get order for meters with stats
        if (currentSort.direction === OrderByDirection.desc) {
          sortedUuids = Object.keys(stats).sort(
            (keyA, keyB) => stats[keyB].volume - stats[keyA].volume
          );
        } else {
          sortedUuids = Object.keys(stats).sort(
            (keyA, keyB) => stats[keyA].volume - stats[keyB].volume
          );
        }
        sortedUuids = sortedUuids.filter(
          (shUuid) => showerheadUuids.indexOf(shUuid) >= 0
        );
        // prepare showerheads without stats
        sortedUuids.map((shUuid) => {
          if (notOrderedShowerheadUuids.indexOf(shUuid) >= 0) {
            notOrderedShowerheadUuids.splice(
              notOrderedShowerheadUuids.indexOf(shUuid),
              1
            );
          }
        });
        // build ordered showerheads uuid list from order direction
        if (currentSort.direction === OrderByDirection.asc) {
          orderedShowerheadUuids = [
            ...notOrderedShowerheadUuids,
            ...sortedUuids,
          ];
        } else {
          orderedShowerheadUuids = [
            ...sortedUuids,
            ...notOrderedShowerheadUuids,
          ];
        }
        if (orderedShowerheadUuids.length > 0) {
          // build ordered showerheads list
          const orderedShowerheads = [];
          orderedShowerheadUuids.map((shUuid) => {
            orderedShowerheads.push(showerheadsById[shUuid]);
          });
          filteredShowerheads = orderedShowerheads;
        }
      } else if (currentSort.field === "label") {
        if (currentSort.direction === OrderByDirection.asc) {
          filteredShowerheads = filteredShowerheads.sort((shA, shB) =>
            shA.universalLabel?.localeCompare(shB.universalLabel)
          );
        } else {
          filteredShowerheads = filteredShowerheads.sort((shA, shB) =>
            shB.universalLabel?.localeCompare(shA.universalLabel)
          );
        }
      } else if (currentSort.field === "last_seen") {
        // console.log(LOG_TAG + "SORT BY LAST_SEEN", currentSort);
        if (currentSort.direction === OrderByDirection.asc) {
          filteredShowerheads = filteredShowerheads.sort(
            (shA, shB) => shB.lastSeenDuration - shA.lastSeenDuration
          );
        } else {
          filteredShowerheads = filteredShowerheads.sort(
            (shA, shB) => shA.lastSeenDuration - shB.lastSeenDuration
          );
        }
      }
    }

    console.log(LOG_TAG + "filtered showerheads", filteredShowerheads);

    return filteredShowerheads;
  });

  // ---------------------------
  // --- selection
  // ---------------------------

  private showerheadsSelectedTimer: any;
  private showerheadUrlTimer: any;

  private readonly showerheadsSelected = signal<string[]>([]);
  readonly showerheadsSelected$ = this.showerheadsSelected.asReadonly();

  private readonly unselectRecently = signal<boolean>(false);
  readonly unselectRecently$ = this.unselectRecently.asReadonly();

  readonly showerhead$ = computed(() => {
    const showerheadsById = this.showerheadsById$();
    const deepPlaceShowerheadUuids = this.deepPlaceShowerheadUuids$();
    const showerheadsSelected = this.showerheadsSelected();
    if (
      showerheadsSelected.length !== 1 ||
      Object.keys(showerheadsById).length === 0 ||
      deepPlaceShowerheadUuids.indexOf(showerheadsSelected[0]) < 0
    )
      return null;

    return showerheadsById[showerheadsSelected[0]];
  });

  updateShowerheadsSelected(showerhead_uuids: string[]) {
    //TODO vérifier si showerhead_uuids a changé

    if (this.showerheadsSelectedTimer) {
      clearTimeout(this.showerheadsSelectedTimer);
    }

    this.showerheadsSelectedTimer = setTimeout(() => {
      this.showerheadsSelected.set(showerhead_uuids);
    }, 100);
  }

  selectShowerhead(showerhead_uuid: string) {
    if (this.showerheadsSelected().indexOf(showerhead_uuid) < 0) {
      const selected = [...this.showerheadsSelected()];
      selected.push(showerhead_uuid);
      this.showerheadsSelected.set(selected);
    }
  }

  selectNone() {
    if (this.showerheadsSelected().length > 0) {
      this.showerheadsSelected.set([]);
      this.unselectRecently.set(true);
    }
  }

  unselectShowerhead(showerhead_uuid: string) {
    if (this.showerheadsSelected().indexOf(showerhead_uuid) >= 0) {
      const selected = this.showerheadsSelected().filter(
        (device_uuid) => device_uuid !== showerhead_uuid
      );
      this.unselectRecently.set(true);
      this.showerheadsSelected.set(selected);
    }
  }

  resetUnselectRecently() {
    if (this.unselectRecently() === true) {
      this.unselectRecently.set(false);
    }
  }

  async updateShowerheadToShowInUrl(showerheadUuid: string): Promise<void> {
    if (this.showerheadUrlTimer) {
      clearTimeout(this.showerheadUrlTimer);
    }

    this.showerheadUrlTimer = setTimeout(async () => {
      await this.router.navigate(
        [
          showerheadUuid
            ? "/showerheads/stats/" + showerheadUuid
            : "/showerheads/stats",
        ],
        {
          replaceUrl: true,
          queryParamsHandling: "merge",
        }
      );
    }, 400);
  }

  // ---------------------------
  // --- navigation
  // ---------------------------

  readonly loading$ = computed(() => {
    const showerheadsCount = this.showerheadsCount$();
    const showerheadsLoading = this.showerheadsLoading$();
    const metricsLoading = this.metricsLoading();
    const statsLoading = this.statsLoading();

    if (showerheadsLoading === false) {
      return showerheadsCount > 0 ? metricsLoading || statsLoading : false;
    } else {
      return showerheadsLoading;
    }
  });

  // ---------------------------
  // --- stats
  // ---------------------------

  private statsContext: string;
  private statsLastFetch: Date;
  private statsRefreshDelay: number = 600; // 10 minutes
  private refreshTimer: any;
  private statsNbDevicesLimit = 50; // retreive stats by batch of N devices

  private readonly statsLoading = signal<boolean>(false);
  readonly statsLoading$ = this.statsLoading.asReadonly();
  private readonly statsById = signal<ShowerheadSignalsMap>({});
  private tourStatsById = signal<ShowerheadSignalsMap>({});

  private readonly showerheadsStats = signal<StatsMap>({}); //TODO
  private tourShowerheadsStats = signal<StatsMap>({}); //TODO

  private readonly metrics = signal<MetricElement[]>([]);
  private readonly metricsLoading = signal<boolean>(false);
  readonly metricsLoading$ = this.metricsLoading.asReadonly();

  readonly statsById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourStatsById() : this.statsById();
  });
  readonly showerheadsStats$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourShowerheadsStats() : this.showerheadsStats();
  });

  fetchStats(
    user: User,
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping
  ): void {
    try {
      if (this.statsLoading()) return;
      const showerheadUuids = this.showerheadUuids$();

      this.statsLoading.set(true);

      console.log(LOG_TAG + "fetchStats...");

      const chunkSize = this.statsNbDevicesLimit;
      const numberOfChunks = Math.ceil(showerheadUuids.length / chunkSize);
      const requests = Array.from({ length: numberOfChunks }, (_, index) => {
        const offset = index * chunkSize;
        return this.fetchStatsBatch(
          fromDate,
          toDate,
          graphGrouping,
          chunkSize,
          offset
        );
      });

      forkJoin(requests).subscribe({
        next: (statsArray) => {
          const stats: ShowerheadSignalsMap = {};

          // compute statsArray to stats
          for (const partial of statsArray) {
            for (const key in partial) {
              if (!stats[key]) {
                stats[key] = [];
              }
              stats[key] = stats[key].concat(partial[key]);
            }
          }

          // store new stats
          console.log(LOG_TAG + "NEW STATS : ", stats);
          this.statsById.set(stats);
          if (this.screenMode !== ShScreenMode.Loading) {
            this.statsLoading.set(false);
          }
          this.statsLastFetch = new Date();
        },
        error: (err) => {
          console.error(LOG_TAG + "failed to fetch stats : ", err);
          this.error.set(err.message || "An error occurred.");
          this.statsLoading.set(false);
        },
        complete: () => {},
      });
    } catch (error) {
      console.error(LOG_TAG + "ERROR ON FETCH STATS", error);
    }
  }

  fetchStatsBatch(
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping,
    limit: number,
    offset: number
  ): Observable<ShowerheadSignalsMap> {
    return this.globalStatsService.getShowerheadStatsForStore(
      fromDate,
      toDate,
      graphGrouping,
      limit,
      offset
    );
  }

  refreshStats(
    user: User,
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping,
    uuids: string[],
    tourMode: boolean = false,
    forceRefresh: boolean = false
  ): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    this.refreshTimer = setTimeout(async () => {
      const newContext: string[] = [];
      newContext.push(`fromDate=${fromDate.toISO()}`);
      newContext.push(`toDate=${toDate.toISO()}`);
      newContext.push(`graphGrouping=${graphGrouping}`);
      newContext.push(`userId=${user.id}`);
      newContext.push(`uuids=${uuids.join(",")}`);

      if (
        forceRefresh ||
        this.statsContext !== newContext.join(",") ||
        !this.statsLastFetch ||
        this.utilsService.calculateElapsedSeconds(this.statsLastFetch) >
          this.statsRefreshDelay
      ) {
        // console.log(LOG_TAG +"context changed !!!");

        if (tourMode) {
          console.log(LOG_TAG + "REFRESH STATS TOUR DATA");
          const tourStats = this._prepareTourStats(
            fromDate,
            toDate,
            graphGrouping
          );
          this.tourStatsById.set(tourStats);
        } else {
          console.log(LOG_TAG + "REFRESH STATS DATA");

          // refresh data
          this.fetchStats(user, fromDate, toDate, graphGrouping);
          this.statsContext = newContext.join(",");
        }
      }
    }, 100);
  }

  getShowerheadsStats(showerheadUuid: string): StatsElement {
    const stats = this.showerheadsStats$();

    return Object.keys(stats).indexOf(showerheadUuid) >= 0
      ? stats[showerheadUuid]
      : null;
  }

  getAllMetricsFromShowers(showers: Shower[]): ShowerheadsGraphStats {
    let result = {
      volume: 0, // volume total
      withLive: false,
      nb_showers: 0,
      avg_shower: 0, // volume average
      avg_duration: 0,
      total_shower_per_day: 0, // inutile ici
    } as ShowerheadsGraphStats;
    let durationSum = 0;

    if (!showers || showers.length === 0) return result;

    // compute stats from showers
    showers.map((shower) => {
      if (shower.volume > 0) {
        result.nb_showers++;
        result.volume += shower.volume || 0;
        durationSum += shower.duration || 0;
        if (shower.is_live) {
          result.withLive = true;
        }
      }
    });

    // compute average
    if (result.volume > 0) {
      result.avg_shower =
        Math.round((result.volume / result.nb_showers) * 10) / 10;
    }

    if (durationSum > 0) {
      result.avg_duration =
        Math.round((durationSum / result.nb_showers) * 10) / 10;
    }

    return result;
  }

  getAllMetrics(showerheadUuids: string[]): ShowerheadsGraphStats {
    let stats = {
      volume: 0,
      countByDay: 0,
      duration: 0,
      count: 0,
      withLive: false,
    };
    let count: number = 0;

    let statsById = this.statsById$();
    if (statsById === null || Object.keys(statsById).length === 0) return null;

    // compute metrics from stats
    showerheadUuids.map((shUuid) => {
      const shStats: ShowerheadSignalElement[] = statsById[shUuid];
      if (!shStats || shStats.length === 0) return;

      shStats.map((stat) => {
        stats.volume += stat.volume || 0;
        stats.count += stat.count || 0;
        stats.countByDay += stat.countByDay || 0;
        stats.duration += stat.duration || 0;

        if (stat.withLive === true) {
          stats.withLive = true;
        }
        count++;
      });
    });

    return {
      volume: stats.volume,
      withLive: stats.withLive,
      nb_showers: stats.count,
      avg_shower:
        stats.volume > 0
          ? Math.round((stats.volume / stats.count) * 10) / 10
          : 0,
      avg_duration:
        stats.duration > 0 ? Math.round((stats.duration / count) * 10) / 10 : 0,
      total_shower_per_day:
        stats.countByDay > 0
          ? Math.round((stats.countByDay / count) * 10) / 10
          : 0,
    } as ShowerheadsGraphStats;
  }

  // ---------------------------
  // --- raw stats
  // ---------------------------

  private refreshRawTimer: any;

  private readonly rawStatsLoading = signal<boolean>(false);
  readonly rawStatsLoading$ = this.rawStatsLoading.asReadonly();
  private readonly showerheadRawStats = signal<Shower[]>([]);
  private tourRawStats = signal<Shower[]>([]);

  readonly showerheadRawStats$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourRawStats() : this.showerheadRawStats();
  });

  fetchRawStats(shUuid: string, fromDate: DateTime, toDate: DateTime): void {
    try {
      if (this.rawStatsLoading()) return;

      this.rawStatsLoading.set(true);

      console.log(LOG_TAG + "fetchRawStats...");

      this.showerService
        .getShowerheadRawStatsForStore(shUuid, fromDate, toDate)
        .subscribe({
          next: (showers: Shower[]) => {
            console.log(LOG_TAG + "NEW RAW STATS : ", showers);
            this.showerheadRawStats.set(showers);
            if (this.screenMode !== ShScreenMode.Loading) {
              this.rawStatsLoading.set(false);
            }
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to fetch raw stats : ", err);
            this.error.set(err.message || "An error occurred.");
            this.rawStatsLoading.set(false);
          },
        });
    } catch (error) {
      console.error(LOG_TAG + "ERROR ON FETCH RAW STATS", error);
    }
  }

  refreshRawStats(
    shUuid: string,
    fromDate: DateTime,
    toDate: DateTime,
    tourMode: boolean
  ) {
    if (this.refreshRawTimer) {
      clearTimeout(this.refreshRawTimer);
    }

    this.refreshRawTimer = setTimeout(async () => {
      if (tourMode) {
        console.log(LOG_TAG + "REFRESH RAW STATS TOUR DATA");
        this.tourRawStats.set(this.generateShowerheadShowers(fromDate, toDate));
      } else {
        console.log(LOG_TAG + "REFRESH RAW STATS");
        this.fetchRawStats(shUuid, fromDate, toDate);
      }
    });
  }

  // ---------------------------
  // --- dashboard stats
  // ---------------------------

  private last30dStatsLastFetch: Date;
  private last30dStatsRefreshDelay: number = 600; // 10 minutes

  private readonly last30dStatsLoading = signal<boolean>(false);
  readonly last30dStatsLoading$ = this.last30dStatsLoading.asReadonly();
  private readonly last30dStatsById = signal<ShowerheadSignalsMap>({});
  private tourLast30dStatsById = signal<ShowerheadSignalsMap>({});

  private readonly showerheadsLast30dStats = signal<StatsMap>({});
  private tourShowerheadsLast30dStats = signal<StatsMap>({});

  readonly last30dStatsById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourLast30dStatsById() : this.last30dStatsById();
  });
  readonly showerheadsLast30dStats$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode
      ? this.tourShowerheadsLast30dStats()
      : this.showerheadsLast30dStats();
  });
  readonly todayActiveShowerheadUuids$ = computed(() => {
    // const last30dStatsById = this.last30dStatsById$();

    // return this._computeActiveShowerheadsFromLast30dStats(last30dStatsById);
    const showerheadsById = this.showerheadsById$();

    const activeUuids = [];

    const now = DateTime.local();
    Object.entries(showerheadsById).forEach(([uuid, sh]) => {
      if (sh.last_seen !== null && sh.last_seen.hasSame(now, "day")) {
        if (activeUuids.indexOf(uuid) < 0) {
          activeUuids.push(uuid);
        }
      }
    });

    return activeUuids;
  });

  fetchLast30dStats(forced: boolean = false): void {
    try {
      if (this.last30dStatsLoading()) return;

      const tourMode = untracked(this.appStore.tourMode$);

      const toDate = DateTime.utc().set({
        hour: 23,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      const fromDate = toDate.minus({ days: 30 });
      const graphGrouping = GraphGrouping.days;

      if (
        forced ||
        !this.last30dStatsLastFetch ||
        this.utilsService.calculateElapsedSeconds(this.last30dStatsLastFetch) >
          this.last30dStatsRefreshDelay
      ) {
        if (tourMode) {
          const tourStats = this._prepareTourStats(
            fromDate,
            toDate,
            graphGrouping
          );
          this.tourLast30dStatsById.set(tourStats);
          this.tourShowerheadsLast30dStats.set(
            this._computeShowerheadStats(tourStats)
          );
        } else {
          this.last30dStatsLoading.set(true);

          console.log(LOG_TAG + "fetchLast30dStats...");

          console.log(
            LOG_TAG + "fetchLast30dStats",
            fromDate,
            toDate,
            GraphGrouping
          );

          this.globalStatsService
            .getShowerheadStatsForStore(fromDate, toDate, graphGrouping)
            .subscribe({
              next: (stats: ShowerheadSignalsMap) => {
                console.log(
                  LOG_TAG + "fetchLast30dStats / NEW LAST 30D STATS : ",
                  stats
                );
                this.last30dStatsById.set(stats);
                this.showerheadsLast30dStats.set(
                  this._computeShowerheadStats(stats)
                );
                if (this.screenMode !== ShScreenMode.Loading) {
                  this.last30dStatsLoading.set(false);
                }
                this.last30dStatsLastFetch = new Date();
              },
              error: (err) => {
                console.error(
                  LOG_TAG + "failed to fetch last 30d stats : ",
                  err
                );
                this.error.set(err.message || "An error occurred.");
                this.last30dStatsLoading.set(false);
              },
            });
        }
      }
    } catch (error) {
      console.error(LOG_TAG + "ERROR ON FETCH LAST 30D STATS", error);
    }
  }

  getShowerheadsLast30dStats(shUuid: string): StatsElement {
    const stats = this.showerheadsLast30dStats$();

    return Object.keys(stats).indexOf(shUuid) >= 0 ? stats[shUuid] : null;
  }

  getPlaceLast30dStats(placeId: number): number {
    let result = 0;
    const showerheads = this.getShowerheadsFromPlaceId(placeId);
    showerheads.map((showerhead) => {
      const stats: StatsElement = this.getShowerheadsLast30dStats(
        showerhead.device_uuid
      );

      if (stats && stats.volume > 0) {
        result += stats.volume;
      }
    });

    return result;
  }

  getPlaceTodayStats(placeId: number): number {
    let result = 0;
    const showerheads = this.getShowerheadsFromPlaceId(placeId);
    const last30dStatsById = this.last30dStatsById$();

    showerheads.map((showerhead) => {
      const showerheadStats: ShowerheadSignalElement[] =
        last30dStatsById[showerhead.device_uuid];

      if (Array.isArray(showerheadStats) && showerheadStats.length > 0) {
        const lastDayStats: ShowerheadSignalElement =
          showerheadStats[showerheadStats.length - 1];

        if (lastDayStats.date.hasSame(DateTime.local(), "day")) {
          result += lastDayStats.volume;
        }
      }
    });

    return result;
  }

  // ---------------------------
  // --- LIFE CYCLE METHODS
  // ---------------------------

  public constructor() {
    this._prepareTourData();

    effect(
      () => {
        const showerheadsById = this._showerheadsById$();
        const placeId = this.filtersStore.placeId$();

        this.unselectRecently.set(false);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const statsById = this.statsById$();
        const tourMode = untracked(this.appStore.tourMode$);
        const user = this.userStore.user$();

        if (tourMode) {
          this.tourShowerheadsStats.set(
            this._computeShowerheadStats(statsById, user)
          );
        } else {
          this.showerheadsStats.set(
            this._computeShowerheadStats(statsById, user)
          );
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const showerheadsById = this._showerheadsById$();
        const gatewaysById = this.gatewayStore.gatewaysById$();
        const placesById = this.placesStore.placesById$();

        const shsDataById: Record<string, ShowerheadData> = {};

        if (Object.keys(showerheadsById).length === 0) {
          this.showerheadsDataById.set(shsDataById);
          return;
        }

        console.log(
          LOG_TAG + "EFFECT refresh shopwerheads data from gateways & places",
          showerheadsById
        );

        Object.entries(showerheadsById).map(([uuid, sh]) => {
          const data: ShowerheadData = {
            placeLabel: null,
            gatewayLabel: null,
          };

          if (sh.gateway_uuid) {
            data.gatewayLabel = this.gatewayStore.getLabel(sh.gateway_uuid);
          }

          if (sh.place_id) {
            data.placeLabel = this.placesStore.getLabel(sh.place_id);
          }

          shsDataById[sh.device_uuid] = data;
        });

        this.showerheadsDataById.set(shsDataById);

        console.log(
          LOG_TAG +
            "EFFECT refresh shopwerheads data from gateways & places => ",
          shsDataById
        );
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const user = this.userStore.user$();
        const showerheads = this.showerheads$();
        let maxThreshold = 0;

        if (showerheads.length === 0) {
          this.maxThreshold.set(maxThreshold);
          return;
        }

        showerheads.map((showerhead) => {
          // prepare threshold with units
          showerhead.prepareThresholds(this.utilsService, user);

          // compute max threshold
          if (showerhead.thresholdPending === true) {
            showerhead.threshold_request.map((threshold) => {
              if (threshold.liter > maxThreshold) {
                maxThreshold = threshold.liter;
              }
            });
          } else if (Array.isArray(showerhead.threshold)) {
            showerhead.threshold.map((threshold) => {
              if (threshold.liter > maxThreshold) {
                maxThreshold = threshold.liter;
              }
            });
          }
          this.maxThreshold.set(maxThreshold);
        });
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const logged = this.authStore.logged$();
        const fromDate = this.filtersStore.dateFrom$();
        const toDate = this.filtersStore.dateTo$();
        const graphGrouping = this.filtersStore.graphGrouping$();
        const user = this.userStore.user$();
        const showerheadUuids = this.showerheadUuids$();
        const showerhead = this.showerhead$();

        const tourMode = untracked(this.appStore.tourMode$);
        const showerheadsSelected = this.showerheadsSelected();
        const forcedRefresh = this.forceRefresh();

        if (!logged) return;

        let nbShowerheads = 0;
        if (Array.isArray(showerheadUuids) && showerheadUuids.length > 0) {
          nbShowerheads = showerheadUuids.length;
        }

        if (
          user &&
          nbShowerheads > 0 &&
          fromDate &&
          toDate &&
          graphGrouping !== GraphGrouping.raw
        ) {
          console.log(LOG_TAG + "EFFECT => refresh stats from filters");

          this.refreshStats(
            user,
            fromDate,
            toDate,
            graphGrouping,
            showerheadUuids,
            tourMode,
            forcedRefresh
          );
        } else if (
          nbShowerheads > 0 &&
          graphGrouping === GraphGrouping.raw &&
          showerhead
        ) {
          console.log(
            LOG_TAG + "EFFECT => refresh raw showerhead signals here ????"
          );
          this.refreshRawStats(
            showerheadsSelected[0],
            fromDate,
            toDate,
            tourMode
          );
        }

        // if (!tourMode) {
        // refresh showerheads if necessary

        if (forcedRefresh === true) {
          this.fetchShowerheads(forcedRefresh);

          this.forceRefresh.set(false);
        }
        // }
      },
      { allowSignalWrites: true }
    );
  }

  getShowerheadsFromPlaceId(placeId: number): ShowerHead[] {
    if (placeId === null) {
      return this.showerheads$().map((sh) => sh.clone());
    } else {
      let showerheadsByPlaceId = this.showerheadsByPlaceId$();

      return showerheadsByPlaceId[placeId] || [];
    }
  }

  triggerForcedRefresh(): void {
    this.forceRefresh.set(true);
  }

  getGlobalGraphData(
    shUuids: string[],
    dateFrom: DateTime,
    dateTo: DateTime,
    grouping: GraphGrouping
  ): ShowerheadSignalElement[] {
    const result: ShowerheadSignalElement[] = [];

    const statsById = this.statsById$();
    if (
      statsById === null ||
      Object.keys(statsById).length === 0 ||
      grouping === GraphGrouping.raw
    )
      return result;

    let addQty = null;
    let compareQty = null;
    switch (grouping) {
      case GraphGrouping.months:
        addQty = "months";
        compareQty = "month";
        break;
      case GraphGrouping.days:
        addQty = "days";
        compareQty = "day";
        break;
      case GraphGrouping.hours:
        addQty = "hours";
        compareQty = "hour";
        break;
    }
    console.log(LOG_TAG + "getGlobalGraphData unit : ", grouping, compareQty);

    // consolidate stats for defined showerheads for each time bar
    for (
      let barDate: DateTime = dateFrom;
      barDate <= dateTo;
      barDate = barDate.plus({ [addQty]: 1 })
    ) {
      let stats = {
        volume: 0,
        countByDay: 0,
        duration: 0,
        count: 0,
        withLive: false,
      };
      let count: number = 0;

      shUuids.map((shUuid) => {
        const statsSh: ShowerheadSignalElement[] = statsById[shUuid];

        if (!statsSh) return;
        const dateStatsSh = statsSh.find((stats) =>
          stats.date.hasSame(barDate, compareQty)
        );
        if (dateStatsSh) {
          stats.volume += dateStatsSh.volume;
          stats.countByDay += dateStatsSh.countByDay;
          stats.duration += dateStatsSh.duration;
          stats.count += dateStatsSh.count;
          count++;

          if (dateStatsSh.withLive) {
            stats.withLive = true;
          }
        }
      });

      result.push(
        new ShowerheadSignalElement(
          barDate,
          stats.duration > 0
            ? Math.round((stats.duration / count) * 10) / 10
            : 0,
          stats.volume,
          null,
          null,
          stats.countByDay > 0
            ? Math.round((stats.countByDay / count) * 10) / 10
            : 0,
          stats.count,
          stats.withLive,
          null
        )
      );
    }

    return result;
  }

  getShowerheadGraphData(
    shUuid: string,
    graphGrouping: GraphGrouping
  ): ShowerheadSignalElement[] {
    const result: ShowerheadSignalElement[] = [];

    if (graphGrouping === GraphGrouping.raw) {
      return [];
    } else {
      const statsById = this.statsById$();
      if (statsById === null || Object.keys(statsById).length === 0)
        return result;

      return statsById[shUuid] ? statsById[shUuid] : [];
    }
  }

  public clear() {
    // reset local values
    this.showerheadsLastFetch.set(null);
    this.statsLastFetch = null;
    this.last30dStatsLastFetch = null;
    this.statsContext = null;
    this.statsContext = null;

    // reset debounces
    clearTimeout(this.refreshTimer);
    clearTimeout(this.refreshRawTimer);
    clearTimeout(this.showerheadsSelectedTimer);
    clearTimeout(this.showerheadsSearchTimer);
    clearTimeout(this.showerheadsOrderByTimer);
    clearTimeout(this.showerheadUrlTimer);

    // reset states
    this.metricsLoading.set(false);
    this.metrics.set([]);

    this.statsLoading.set(false);
    this.statsById.set({});
    this.showerheadsStats.set({});

    this.rawStatsLoading.set(false);
    this.showerheadRawStats.set([]);

    this.last30dStatsLoading.set(false);
    this.last30dStatsById.set({});
    this.showerheadsLast30dStats.set({});

    this.showerheads.set([]);
    this.showerheadsById.set({});
    this.showerheadsLoading.set(false);
    this.showerheadsDataById.set({});

    this.adding.set(false);
    this.addResult.set(null);

    this.updating.set(false);
    this.updateResult.set(null);

    // this.meterRemoving.set(false);
    // this.meterRemoveResult.set(null);

    this.showerheadsOrderBy.set("label-asc");
    this.showerheadsSearch.set(null);
    this.showerheadsSelected.set([]);
    this.unselectRecently.set(false);

    this.error.set(null);
  }

  // ---- PRIVATE METHODS ------

  private _computeShowerheadStats(
    statsById: ShowerheadSignalsMap,
    user: User = null
  ): StatsMap {
    let result: StatsMap = {};

    Object.entries(statsById).forEach(([shUuid, stats]) => {
      let volume: number = 0;
      let flow: number = 0;
      let temperature: number = 0;
      let count = 0;
      stats.map((stat) => {
        volume += stat.volume;
        temperature += stat.temperature;
        flow += stat.flow;
        count++;
      });

      const elem = new StatsElement(
        this.utilsService,
        parseFloat(volume.toFixed(2)),
        null,
        count > 0 ? Math.round(flow / count) : null,
        null,
        count > 0 ? Math.round(temperature / count) : null,
        null
      );
      if (user) {
        elem.updateValuesWithUnitsWithUser(user);
      }

      result[shUuid] = elem;
    });

    return result;
  }

  private _computeIndexedShowerheads(
    showerheads: ShowerHead[]
  ): Record<string, ShowerHead> {
    const index: Record<string, ShowerHead> = {};

    // indexing showerheads
    showerheads.forEach(
      (showerhead) => (index[showerhead.device_uuid] = showerhead)
    );

    return index;
  }

  private _computeShowerheadsByPlaceId(
    indexedShowerheads: Record<string, ShowerHead>
  ): Record<number, ShowerHead[]> {
    const index: Record<number, ShowerHead[]> = {};

    for (const device_uuid in indexedShowerheads) {
      const showerhead: ShowerHead = indexedShowerheads[device_uuid];
      let placeId: any = showerhead.place_id;
      if (showerhead.place_id === null) {
        placeId = -1;
      }
      if (!index[placeId]) {
        index[placeId] = [];
      }
      index[placeId].push(showerhead);
    }

    return index;
  }

  private _computeActiveShowerheadsFromLast30dStats(
    last30dStatsById: ShowerheadSignalsMap
  ): string[] {
    const activeShowerheadUuids = [];

    Object.entries(last30dStatsById).map(([shUuid, shStats]) => {
      const lastDayStats: ShowerheadSignalElement = shStats[shStats.length - 1];

      if (lastDayStats && lastDayStats.date.hasSame(DateTime.local(), "day")) {
        if (lastDayStats.volume > 0) {
          activeShowerheadUuids.push(shUuid);
        }
      }
    });

    return activeShowerheadUuids;
  }

  // ---- TOUR METHODS --------

  private _prepareTourStats(
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping
  ): ShowerheadSignalsMap {
    //TODO
    let tourStats: ShowerheadSignalsMap = {};

    Object.entries(this.tourShowerheadsById).forEach(([shUuid, showerhead]) => {
      tourStats[shUuid] = this.generateShowerheadStats(
        fromDate,
        toDate,
        graphGrouping
      );
    });

    return tourStats;
  }

  public generateShowerheadShowers(
    fromDate: DateTime,
    toDate: DateTime
  ): Shower[] {
    const result: Shower[] = [];

    const maxShowerVolume = 100;
    const maxNbShowers = 6;
    const maxShowerDuration = 15 * 60; // 15 mins
    const maxSoapingDuration = 2.5 * 60;
    const maxNbSoapings = 3;
    const minutesStep = Math.round(60 / maxNbShowers);

    const nbShowers = Math.floor(Math.random() * maxNbShowers);
    for (let showerIndex = 0; showerIndex < nbShowers; showerIndex++) {
      result.push(
        new Shower(
          18 + showerIndex,
          null,
          Math.floor(Math.random() * maxShowerVolume),
          parseFloat((Math.random() * (28 - 20) + 20).toFixed(2)), // between 20 and 28
          parseFloat((Math.random() * 4.5).toFixed(2)),
          Math.floor(Math.random() * maxShowerDuration),
          Math.floor(Math.random() * maxSoapingDuration),
          Math.floor(Math.random() * maxNbSoapings),
          fromDate.plus({
            minutes:
              minutesStep * showerIndex +
              Math.floor(Math.random() * minutesStep),
          }),
          Math.floor(Math.random() * 1) === 1,
          showerIndex === nbShowers - 1
        )
      );
    }

    return result;
  }

  public generateShowerheadStats(
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping
  ): ShowerheadSignalElement[] {
    const result: ShowerheadSignalElement[] = [];

    let maxDayVolume = 30;
    let nbDays = Math.floor(Math.abs(toDate.diff(fromDate, "days").days));
    if (nbDays === 0) {
      nbDays = 1;
      maxDayVolume = 20;
    }

    let maxBars = 30;
    let maxShowers = 10;
    switch (graphGrouping) {
      case GraphGrouping.days:
        maxBars = 30;
        maxShowers = 10;
        break;
      case GraphGrouping.months:
        maxBars = 12;
        maxShowers = 300;
        break;
      case GraphGrouping.hours:
        maxBars = 24;
        maxShowers = 5;
        break;
      case GraphGrouping.raw:
        maxBars = 60; //TODO que mettre ici / nombre max de douches dans 1 heure ?
        maxShowers = maxBars;
        break;
    }
    const nbBars = Math.floor(Math.random() * maxBars);
    const nbShowersByDay = Math.floor(Math.random() * 10);
    const avgDuration = Math.floor(Math.random() * 6 * 60);
    const nbShowers = Math.floor(Math.random() * maxShowers);

    for (let barIndex = 0; barIndex < nbBars; barIndex++) {
      let barDate: DateTime = toDate.minus({ days: nbBars - barIndex });
      switch (graphGrouping) {
        case GraphGrouping.days:
          barDate = fromDate.plus({ days: barIndex });
          break;
        case GraphGrouping.months:
          barDate = fromDate.plus({ months: barIndex });
          break;
        case GraphGrouping.hours:
          barDate = fromDate.plus({ hours: barIndex });
          break;
        case GraphGrouping.raw:
          //TODO que mettre ici ???
          barDate = fromDate.plus({ minutes: barIndex });
          break;
      }

      const dayVolume = Math.floor(Math.random() * maxDayVolume);
      const barVolume = dayVolume * nbShowers;

      // entre 20 et 28
      const barTemperature =
        dayVolume > 0
          ? parseFloat((Math.random() * (28 - 20) + 20).toFixed(2))
          : 0;

      // entre 0 et 4.5
      const barFlow =
        dayVolume > 0 ? parseFloat((Math.random() * 4.5).toFixed(2)) : 0;

      result.push(
        new ShowerheadSignalElement(
          barDate,
          avgDuration,
          barVolume,
          barFlow,
          barTemperature,
          nbShowersByDay,
          nbShowers,
          false,
          null
        )
      );
    }

    return result;
  }

  private _prepareUnplacedShowerheads(): void {
    // showerheads without place
    const swimmingPoolData = ["01", "Swimming Pool", null];

    const swimmingPool = this._createShowerhead(
      swimmingPoolData[0],
      swimmingPoolData[1],
      swimmingPoolData[2] !== null ? parseInt(swimmingPoolData[2]) : null,
      // DateTime.local().minus({ minutes: 3 }),
      null,
      ShowerHeadType.aloe,
      [
        new Threshold(10, ThresholdColor.green),
        new Threshold(20, ThresholdColor.blue),
        new Threshold(30, ThresholdColor.light_blue),
        new Threshold(40, ThresholdColor.red),
      ],
      ShowerHeadConnectivity.lora,
      "00005813d3093eb7"
    );

    this.tourShowerheads.push(swimmingPool);
  }

  private _prepareGrenobleShowerheads(shScenario: ShowerheadTourScenario) {
    const grenoblePlaceId = "1";
    let grenobleHomeFloor0 = "6";
    let grenobleHomeFloor1 = "7";
    if (shScenario == ShowerheadTourScenario.One) {
      grenobleHomeFloor0 = grenoblePlaceId;
      grenobleHomeFloor1 = grenoblePlaceId;
    }

    const aloeFloor0Data = ["21", "Aloe_floor0G", grenobleHomeFloor0];
    const yuccaFloor0Data = ["22", "Yucca_floor0G", grenobleHomeFloor0];

    const aloeFloor1Data = ["23", "Aloe_floor1G", grenobleHomeFloor1];

    const aloeFloor0 = this._createShowerhead(
      aloeFloor0Data[0],
      aloeFloor0Data[1],
      parseInt(aloeFloor0Data[2]),
      DateTime.local().minus({ months: 5 }),
      ShowerHeadType.aloe,
      [
        new Threshold(10, ThresholdColor.green),
        new Threshold(20, ThresholdColor.blue),
        new Threshold(30, ThresholdColor.yellow),
        new Threshold(40, ThresholdColor.red),
      ],
      ShowerHeadConnectivity.lora,
      "008b4fcc92ca122baa8659b584d180b8"
    );

    const yuccaFloor0 = this._createShowerhead(
      yuccaFloor0Data[0],
      yuccaFloor0Data[1],
      parseInt(yuccaFloor0Data[2]),
      DateTime.local().minus({ minutes: 28 }),
      ShowerHeadType.yucca,
      [
        new Threshold(12, ThresholdColor.green),
        new Threshold(25, ThresholdColor.blue),
        new Threshold(38, ThresholdColor.orange),
        new Threshold(50, ThresholdColor.red),
      ],
      ShowerHeadConnectivity.ble,
      "008b4fcc92ca122baa8659b584d180b8"
    );

    const aloeFloor1 = this._createShowerhead(
      aloeFloor1Data[0],
      aloeFloor1Data[1],
      parseInt(aloeFloor1Data[2]),
      DateTime.local().minus({ years: 1 }),
      ShowerHeadType.aloe,
      [
        new Threshold(8, ThresholdColor.green),
        new Threshold(18, ThresholdColor.purple),
        new Threshold(28, ThresholdColor.yellow),
        new Threshold(38, ThresholdColor.red),
      ],
      ShowerHeadConnectivity.lora,
      "008b4fcc92ca122baa8659b584d180b8"
    );

    this.tourShowerheads.push(aloeFloor0);
    this.tourShowerheads.push(yuccaFloor0);
    this.tourShowerheads.push(aloeFloor1);
  }

  private _prepareShowerheadsTest(): void {
    const threshold1 = [
      new Threshold(8, ThresholdColor.green),
      new Threshold(18, ThresholdColor.purple),
      new Threshold(28, ThresholdColor.yellow),
      new Threshold(38, ThresholdColor.red),
    ];

    const threshold2 = [
      new Threshold(12, ThresholdColor.green),
      new Threshold(25, ThresholdColor.blue),
      new Threshold(38, ThresholdColor.orange),
      new Threshold(50, ThresholdColor.red),
    ];

    const threshold3 = [
      new Threshold(10, ThresholdColor.green),
      new Threshold(20, ThresholdColor.blue),
      new Threshold(30, ThresholdColor.yellow),
      new Threshold(40, ThresholdColor.red),
    ];

    const aloeChamber01 = this._createShowerhead(
      "01",
      "Aloe chamber 01",
      3,
      DateTime.local().minus({ years: 1 }),
      ShowerHeadType.aloe,
      threshold1,
      ShowerHeadConnectivity.lora,
      "00005813d3093eb2"
    );
    const aloeChamber02 = this._createShowerhead(
      "02",
      "Aloe chamber 02",
      3,
      DateTime.local().minus({ months: 1 }),
      ShowerHeadType.aloe,
      threshold2,
      ShowerHeadConnectivity.lora,
      "00005813d3093eb2"
    );
    const cereusChamber03 = this._createShowerhead(
      "03",
      "Aloe chamber 03",
      3,
      DateTime.local().minus({ minutes: 65 }),
      ShowerHeadType.cereus,
      threshold2,
      ShowerHeadConnectivity.lora,
      "00005813d3093eb2"
    );
    const aloeChamber11 = this._createShowerhead(
      "04",
      "Aloe chamber 11",
      4,
      DateTime.local().minus({ months: 10 }),
      ShowerHeadType.aloe,
      threshold3,
      ShowerHeadConnectivity.lora,
      "00005813d3093eb3"
    );
    const cereusChamber12 = this._createShowerhead(
      "05",
      "Aloe chamber 12",
      4,
      DateTime.local().minus({ second: 37 }),
      ShowerHeadType.cereus,
      threshold1,
      ShowerHeadConnectivity.lora,
      "00005813d3093eb3"
    );
    const aloeNoPlace = this._createShowerhead(
      "06",
      "My Aloe",
      null,
      DateTime.local().minus({ hours: 10 }),
      ShowerHeadType.aloe,
      threshold1,
      ShowerHeadConnectivity.lora,
      "00005813d3093eb1"
    );
    const cereusNoPlace = this._createShowerhead(
      "07",
      "My Cereus",
      null,
      DateTime.local().minus({ seconds: 10 }),
      ShowerHeadType.cereus,
      threshold1,
      ShowerHeadConnectivity.lora,
      "00005813d3093eb1"
    );
    const yuccaNoPlace = this._createShowerhead(
      "08",
      "My Yucca",
      null,
      DateTime.local().minus({ hours: 10 }),
      ShowerHeadType.yucca,
      threshold3,
      ShowerHeadConnectivity.ble,
      "008b4fcc92ca122baa8659b584d180b1"
    );
    const aloeChamber13 = this._createShowerhead(
      "09",
      "Aloe chamber 13",
      4,
      DateTime.local().minus({ minutes: 19 }),
      ShowerHeadType.aloe,
      threshold1,
      ShowerHeadConnectivity.ble,
      "008b4fcc92ca122baa8659b584d180b2"
    );
    const yuccaChamber14 = this._createShowerhead(
      "10",
      "Yucca chamber 14",
      4,
      DateTime.local().minus({ hours: 19 }),
      ShowerHeadType.yucca,
      threshold2,
      ShowerHeadConnectivity.ble,
      "008b4fcc92ca122baa8659b584d180b2"
    );
    const showerheadGrenoble = this._createShowerhead(
      "11",
      "Home",
      1,
      DateTime.local().minus({ days: 19 }),
      ShowerHeadType.aloe,
      threshold2,
      ShowerHeadConnectivity.ble,
      "008b4fcc92ca122baa8659b584d180b4"
    );

    this.tourShowerheads.push(aloeChamber01);
    this.tourShowerheads.push(aloeChamber02);
    this.tourShowerheads.push(cereusChamber03);
    this.tourShowerheads.push(aloeChamber11);
    this.tourShowerheads.push(cereusChamber12);
    this.tourShowerheads.push(aloeNoPlace);
    this.tourShowerheads.push(yuccaNoPlace);
    this.tourShowerheads.push(cereusNoPlace);
    this.tourShowerheads.push(aloeChamber13);
    this.tourShowerheads.push(yuccaChamber14);
    this.tourShowerheads.push(showerheadGrenoble);
  }

  private _prepareTourData() {
    switch (this.appStore.showerheadsScenario) {
      case ShowerheadTourScenario.None:
        // nothing to do
        break;
      case ShowerheadTourScenario.One:
        this._prepareUnplacedShowerheads();
        break;
      case ShowerheadTourScenario.Full:
        switch (this.appStore.placesScenario) {
          case PlaceTourScenario.Full:
            this._prepareUnplacedShowerheads();
            this._prepareGrenobleShowerheads(this.appStore.showerheadsScenario);
            break;
          case PlaceTourScenario.One:
            this._prepareUnplacedShowerheads();
            this._prepareGrenobleShowerheads(this.appStore.showerheadsScenario);
            break;
          case PlaceTourScenario.None:
            this._prepareUnplacedShowerheads();
            break;
        }
        break;
      case ShowerheadTourScenario.Test:
        this._prepareShowerheadsTest();
        break;
    }

    // prepare showerheads
    this.tourShowerheadsById = this._computeIndexedShowerheads(
      this.tourShowerheads
    );
    console.log(LOG_TAG + "TOUR SHOWERHEADS BY ID", this.tourShowerheadsById);
  }

  private _createShowerhead(
    id: string,
    label: string,
    placeId: number,
    last_seen: DateTime,
    type: ShowerHeadType = ShowerHeadType.aloe,
    thresholds: Threshold[] = null,
    connectivity: ShowerHeadConnectivity = ShowerHeadConnectivity.ble,
    gateway_uuid?: string
  ): ShowerHead {
    return new ShowerHead(
      "50fd937d-6133-41ad-bfa5-dc47de05bc" + id,
      DateTime.local().minus({ years: 3 }),
      last_seen,
      thresholds,
      null,
      label,
      "22_21_0" + id,
      type,
      connectivity,
      "9",
      "90040400",
      placeId,
      gateway_uuid
    );
  }
}
